import { useState, useEffect, useCallback, useRef } from 'react';
import { Web } from 'sip.js';
import { io } from 'socket.io-client';
import { useWebRTCContext } from './WebRTCContext';
import { 
  MOCK_SIP_DOMAIN, 
  MAX_RECONNECT_ATTEMPTS,
  RECONNECT_DELAY
} from '../../constants/webrtc';

/**
 * Custom hook to manage WebRTC connection to Dasha AI
 * 
 * @param {Object} config - Configuration options
 * @param {string} config.userName - User's name for personalization
 * @returns {Object} - WebRTC connection controls and state
 */
const useWebRTC = ({ userName }) => {
  const { state, dispatch } = useWebRTCContext();
  const [sipUser, setSipUser] = useState(null);
  const [sipSession, setSipSession] = useState(null);
  const [socket, setSocket] = useState(null);
  const [microphoneAccess, setMicrophoneAccess] = useState(null);
  const [audioContext, setAudioContext] = useState(null);
  const [audioAnalyser, setAudioAnalyser] = useState(null);
  const [mediaStream, setMediaStream] = useState(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isCallSetupComplete, setIsCallSetupComplete] = useState(false);
  
  // Refs for managing resources that need cleanup
  const audioLevelIntervalRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  
  // Refs for state values to use in callbacks to avoid stale closures
  const isMutedRef = useRef(isMuted);
  useEffect(() => { isMutedRef.current = isMuted; }, [isMuted]);

  const mediaStreamRef = useRef(mediaStream);
  useEffect(() => { mediaStreamRef.current = mediaStream; }, [mediaStream]);

  const audioContextRef = useRef(audioContext);
  useEffect(() => { audioContextRef.current = audioContext; }, [audioContext]);
  
  const audioAnalyserRef = useRef(audioAnalyser); 
  useEffect(() => { audioAnalyserRef.current = audioAnalyser; }, [audioAnalyser]);

  const sipUserRef = useRef(sipUser);
  useEffect(() => { sipUserRef.current = sipUser; }, [sipUser]);

  const sipSessionRef = useRef(sipSession); 
  useEffect(() => { sipSessionRef.current = sipSession; }, [sipSession]);
  
  const connectionStateRef = useRef(state.connectionState); 
  useEffect(() => { connectionStateRef.current = state.connectionState; }, [state.connectionState]);
  
  // Get SIP credentials from server with fallback for development
  const getSipCredentials = useCallback(async () => {
    // Use provided serverUrl or get from constants
    
    try {
      // Always try to get real credentials from the server first
      console.log(`Fetching SIP credentials from /api/v1/live-demo/sip`);
      const response = await fetch(`/api/v1/live-demo/sip`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to get SIP credentials: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Received SIP credentials:', data);
      return data;
    } catch (error) {
      console.error('Error getting SIP credentials:', error);
      
      // If in development mode or if forced to use mock data, use mock credentials
      if (process.env.NODE_ENV !== 'production') {
        console.warn('Using mock SIP credentials for development');
        const mockUuid = Math.random().toString(36).substring(2, 15);
        
        // Use mock credentials
        return {
          aor: `sip:reg-${mockUuid}@${MOCK_SIP_DOMAIN}`,
          sipServerEndpoint: `wss://sip.${MOCK_SIP_DOMAIN}/sip/connect`
        };
      }
      
      // In production, rethrow the error
      throw error;
    }
  }, []);
  
  // Initialize conversation with server
  const initiateConversation = useCallback(async (aor) => {
    
    try {
      // Send real request to start conversation
      console.log(`Initiating conversation with SIP AOR: ${aor}`);
      const response = await fetch(`/api/v1/live-demo/call`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          aor,
          name: userName || 'User',
        }),
      });
      
      if (!response.ok) {
        throw new Error(`Failed to initiate conversation: ${response.status}`);
      }
      
      // Server responds with 200 OK status on success with no body
      console.log('Conversation initiated successfully');
      return { status: 'success' };
    } catch (error) {
      console.error('Error initiating conversation:', error);
      
      // In development, simulate success
      if (process.env.NODE_ENV !== 'production') {
        console.warn('Using mock conversation initiation for development');
        // Simulate network delay
        await new Promise(resolve => setTimeout(resolve, 700));
        return { status: 'success', mock: true };
      }
      
      throw error;
    }
  }, [userName]);
  
  // Check microphone permissions with enhanced error handling
  const checkMicrophonePermission = useCallback(async () => {
    try {
      // Request microphone access with optimal audio settings for voice
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 16000, // 16kHz sample rate for better speech recognition
          channelCount: 1     // Mono for voice
        } 
      });
      
      setMicrophoneAccess(true);
      setMediaStream(stream);
      return { success: true, stream };
    } catch (error) {
      console.error('Microphone access error:', error);
      setMicrophoneAccess(false);
      
      // Provide specific error messages based on the error type
      let errorMessage = 'Microphone access denied. Please enable microphone access and try again.';
      
      if (error.name === 'NotFoundError') {
        errorMessage = 'No microphone found. Please connect a microphone and try again.';
      } else if (error.name === 'NotAllowedError') {
        errorMessage = 'Microphone access denied. Please allow microphone access in your browser settings.';
      } else if (error.name === 'NotReadableError') {
        errorMessage = 'Cannot access your microphone. It might be in use by another application.';
      } else if (error.name === 'OverconstrainedError') {
        errorMessage = 'The requested audio settings are not supported by your device.';
      } else if (error.name === 'AbortError') {
        errorMessage = 'Hardware error occurred when accessing your microphone.';
      }
      
      dispatch({ 
        type: 'SET_ERROR', 
        payload: { message: errorMessage } 
      });
      
      return { success: false, error };
    }
  }, [dispatch]);
  
  // Initialize audio analyzer for real-time visualization
  const initAudioAnalyzer = useCallback((stream) => {
    if (!stream || isMutedRef.current) {
      if (isMutedRef.current && state.audioLevels.user !== 0) {
        dispatch({ type: 'SET_USER_AUDIO_LEVEL', payload: 0 });
      }
      return null;
    }

    try {
      const context = new (window.AudioContext || window.webkitAudioContext)();
      setAudioContext(context);
      const source = context.createMediaStreamSource(stream);
      const analyser = context.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.8;
      source.connect(analyser);
      setAudioAnalyser(analyser);

      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      if (audioLevelIntervalRef.current) clearInterval(audioLevelIntervalRef.current);

      audioLevelIntervalRef.current = setInterval(() => {
        if (!audioAnalyserRef.current || isMutedRef.current) {
          if (isMutedRef.current && state.audioLevels.user !== 0) {
            dispatch({ type: 'SET_USER_AUDIO_LEVEL', payload: 0 });
          }
          return;
        }
        audioAnalyserRef.current.getByteFrequencyData(dataArray);
        let sum = 0;
        for (let i = 0; i < dataArray.length; i++) sum += dataArray[i];
        const average = sum / dataArray.length;
        
        const NOISE_THRESHOLD_AVG = 8; // Average audio level (0-255 scale) below which is considered noise for the visualizer.
        const SPEECH_VISUAL_MULTIPLIER = 2.0; // How much to amplify speech levels for a more dynamic visualizer.
        const MIN_VISUAL_LEVEL_ON_SPEECH = 10; // Minimum visual level (0-100) to show once speech is detected.

        let level;
        if (average < NOISE_THRESHOLD_AVG) {
          level = 0; // Dispatch 0; AudioVisualizer's createWaveform handles idle animation for level <= 1.
        } else {
          // Scale the portion of the average that is above the noise threshold.
          const speechAverage = average - NOISE_THRESHOLD_AVG;
          // Calculate percentage based on the range above noise up to max possible (255 - NOISE_THRESHOLD_AVG)
          const speechRange = 255 - NOISE_THRESHOLD_AVG;
          let scaledSpeechLevel = (speechAverage / speechRange) * 100;
          
          level = scaledSpeechLevel * SPEECH_VISUAL_MULTIPLIER;
          level = Math.max(level, MIN_VISUAL_LEVEL_ON_SPEECH); // Ensure a minimum jump for detected speech.
        }

        level = Math.min(100, Math.round(level)); // Clamp to 100 and round.

        if (state.audioLevels.user !== level) {
            dispatch({ type: 'SET_USER_AUDIO_LEVEL', payload: level });
        }
      }, 100);

      return analyser;
    } catch (error) {
      console.error('Error initializing audio analyzer:', error);
      setAudioContext(null);
      setAudioAnalyser(null);
      return null;
    }
  }, [dispatch, state.audioLevels]);
  
  // Set up remote audio output and handling
  const setupRemoteAudio = useCallback((session) => {
    if (!session) {
      console.error('setupRemoteAudio: Session is undefined');
      return;
    }
    console.log('Setting up remote audio handling...');

    if (!remoteAudioRef.current) {
      const audioElement = new Audio();
      audioElement.autoplay = true;
      audioElement.id = 'dasha-remote-audio';
      // Ensure the audio element is not visible and has no controls by default
      audioElement.style.display = 'none';
      audioElement.controls = false; 
      document.body.appendChild(audioElement);
      remoteAudioRef.current = audioElement;
      console.log('Created hidden remote audio element.');
    }

    const handleTrack = (event) => {
      console.log('Received remote audio track event:', event);
      const [remoteStream] = event.streams;
      if (remoteStream && remoteAudioRef.current) {
        const audioElement = remoteAudioRef.current;
        console.log('Setting remote stream to audio element.');
        if (audioElement.srcObject && audioElement.srcObject !== remoteStream) {
          audioElement.srcObject.getTracks().forEach(track => track.stop());
        }
        audioElement.srcObject = remoteStream;
        audioElement.play().then(() => {
          console.log('Remote audio playback started successfully.');
        }).catch(err => {
          console.error('Error auto-playing remote audio:', err);
          const playOnInteraction = () => {
            if (remoteAudioRef.current) { // Check ref again inside listener
              remoteAudioRef.current.play()
                .then(() => console.log('Remote audio playback started after user interaction.'))
                .catch(e => console.error('Still failed to play remote audio after interaction:', e));
            }
            document.removeEventListener('click', playOnInteraction, true);
          };
          document.addEventListener('click', playOnInteraction, true);
          console.log('Set up click listener to attempt remote audio playback due to autoplay policy.');
        });

        // Setup AI audio level visualization (simplified)
        if (audioContextRef.current && audioContextRef.current.state !== 'closed' && remoteStream.getAudioTracks().length > 0) {
          try {
            const source = audioContextRef.current.createMediaStreamSource(remoteStream);
            const analyser = audioContextRef.current.createAnalyser();
            analyser.fftSize = 256;
            source.connect(analyser);
            // Not setting audioAnalyser state here, as it's for local user audio
            // This interval is just for AI level dispatch
            const remoteDataArray = new Uint8Array(analyser.frequencyBinCount);
            // Note: This interval is automatically cleaned up when the audio element or peer connection is disposed
            setInterval(() => {
              if (analyser && !audioElement.paused) {
                analyser.getByteFrequencyData(remoteDataArray);
                let sum = 0;
                for (let i = 0; i < remoteDataArray.length; i++) sum += remoteDataArray[i];
                const average = sum / remoteDataArray.length;
                // Adjusted AI audio level calculation for consistency, removing the arbitrary boost to 40
                let level = (average / 255) * 100;
                level = level * 2.5; // Amplify slightly less than user, or tune as needed

                // Optional: Boost low AI levels slightly if needed for visibility, but less aggressive
                if (average > 2 && level < 15) {
                  level = Math.max(level, 15);
                }
                
                level = Math.min(100, Math.round(level));

                if (state.audioLevels.ai !== level) { // Only dispatch if level changed
                    dispatch({ type: 'SET_AI_AUDIO_LEVEL', payload: level });
                }
              } else if (audioElement.paused && state.audioLevels.ai !== 0) {
                dispatch({ type: 'SET_AI_AUDIO_LEVEL', payload: 0 });
              }
            }, 100);
            // Store this interval to clear it later if needed, perhaps in session.on('terminated') or cleanupResources
            // For now, it will be cleared with other intervals in cleanupResources if that part is robust.
          } catch (err) {
            console.error('Error setting up remote audio analyzer:', err);
          }
        }
      } else {
        console.warn('handleTrack: No remote stream or audio element found.');
      }
    };

    // Standard way to handle tracks with sip.js Session
    if (session.sessionDescriptionHandler && session.sessionDescriptionHandler.peerConnection) {
      const pc = session.sessionDescriptionHandler.peerConnection;
      pc.ontrack = handleTrack; 
      // Check for existing tracks immediately
      pc.getReceivers().forEach(receiver => {
        if (receiver.track && receiver.track.kind === 'audio') {
          console.log('Processing existing audio track on receiver.');
          handleTrack({ streams: [new MediaStream([receiver.track])] });
        }
      });
    } else {
      console.error('setupRemoteAudio: PeerConnection not available on session to attach ontrack handler.');
    }
    // Cleanup for ontrack when session ends or component unmounts is implicitly handled by PC disposal
  }, [dispatch, state.audioLevels]);
  
  // Set up WebSocket connection for real-time transcription
  const setupTranscriptSocket = useCallback((conversationId) => {
    // Use provided socket URL or get from constants
    // In development, force connection to dasha.ai directly to bypass local server
    const webSocketUrl = process.env.NODE_ENV === 'development' ? 'https://dasha.ai' : "/";
    
    if (!webSocketUrl || !conversationId) {
      console.warn('No socket URL or conversation ID provided for transcript');
      return null;
    }
    
    try {
      console.log(`Connecting to transcript WebSocket at ${webSocketUrl}`);
      
      // Create real socket connection
      const newSocket = io(webSocketUrl, {
        query: { conversationId },
        transports: ['websocket'],
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
      });
      
      // Set up event handlers
      newSocket.on('connect', () => {
        console.log('%c[SOCKET] Connected', 'color: green; font-weight: bold');
        
        // Send join event with sessionId to properly connect to the room
        console.log(`%c[SOCKET] Joining transcript room with session ID: ${conversationId}`, 'color: green');
        newSocket.emit('join', { sessionId: conversationId });
        
        dispatch({ 
          type: 'ADD_TRANSCRIPT_ITEM', 
          payload: { 
            cmd: 'log', 
            text: 'Connected to transcript service.' 
          } 
        });
      });
      
      newSocket.on('connect_error', (error) => {
        console.error('%c[SOCKET] Connection error:', 'color: red; font-weight: bold', error);
        
        dispatch({ 
          type: 'ADD_TRANSCRIPT_ITEM', 
          payload: { 
            cmd: 'error', 
            text: 'Error connecting to transcript service.' 
          } 
        });
      });
      
      // Handle successful join event
      newSocket.on('joined', (data) => {
        console.log('%c[SOCKET] Successfully joined transcript room:', 'color: green; font-weight: bold', data);
        
        dispatch({ 
          type: 'ADD_TRANSCRIPT_ITEM', 
          payload: { 
            cmd: 'log', 
            text: 'Ready to receive live transcription.' 
          } 
        });
      });
      
      // Handle socket error events
      newSocket.on('error', (error) => {
        console.error('%c[SOCKET] Server error:', 'color: red; font-weight: bold', error);
        
        if (error && error.message) {
          dispatch({ 
            type: 'ADD_TRANSCRIPT_ITEM', 
            payload: { 
              cmd: 'error', 
              text: `Server error: ${error.message}` 
            } 
          });
        }
      });
      
      // Listen for transcript events (format matches the API contract)
      newSocket.on('transcript', (data) => {
        console.log('%c[SOCKET] Received transcript update:', 'color: blue; font-weight: bold', data);
        console.log('%c[SOCKET] Session ID used for transcript:', 'color: blue', conversationId);
        
        if (data.content) {
          const role = data.role === 'assistant' ? 'ai' : 'human';
          console.log(`%c[SOCKET] Adding ${role} transcript:`, 'color: blue', data.content.substring(0, 50) + (data.content.length > 50 ? '...' : ''));
          
          dispatch({ 
            type: 'ADD_TRANSCRIPT_ITEM', 
            payload: { 
              cmd: role, 
              text: data.content 
            } 
          });
          
          // Log state after transcript is added for debugging
          if (process.env.NODE_ENV !== 'production') {
            setTimeout(() => {
              console.log('%c[SOCKET] Transcript state update complete', 'color: blue');
            }, 0);
          }
        } else {
          console.warn('%c[SOCKET] Received transcript update without content', 'color: orange', data);
        }
      });
      
      newSocket.on('error', (error) => {
        console.error('%c[SOCKET] Transcript socket error:', 'color: red; font-weight: bold', error);
        
        dispatch({ 
          type: 'ADD_TRANSCRIPT_ITEM', 
          payload: { 
            cmd: 'error', 
            text: 'Error receiving transcript updates.' 
          } 
        });
      });
      
      newSocket.on('disconnect', (reason) => {
        console.log('%c[SOCKET] Transcript socket disconnected:', 'color: orange; font-weight: bold', reason);
        
        dispatch({ 
          type: 'ADD_TRANSCRIPT_ITEM', 
          payload: { 
            cmd: 'log', 
            text: `Transcript connection closed: ${reason}` 
          } 
        });
        
        if (reason === 'io server disconnect') {
          // Disconnected by server, try reconnecting
          console.log('%c[SOCKET] Attempting to reconnect...', 'color: orange');
          newSocket.connect();
        }
      });
      
      return newSocket;
    } catch (error) {
      console.error('Error setting up transcript socket:', error);
      
      // In development, create a mock socket for testing
      if (process.env.NODE_ENV !== 'production') {
        console.warn('%c[SOCKET] Using mock transcript socket for development', 'color: orange; font-weight: bold');
        console.log('%c[SOCKET] Mock socket using conversation ID:', 'color: orange', conversationId);
        
        // Create a mock object with the same interface
        const mockSocket = {
          on: (event, callback) => {
            // Store the callback for our mock implementation to use
            mockSocket.eventHandlers = mockSocket.eventHandlers || {};
            mockSocket.eventHandlers[event] = callback;
          },
          emit: (event, data) => {
            console.log(`Mock socket emitting: ${event}`, data);
            // Auto-respond to join events in mock mode
            if (event === 'join' && data && data.sessionId) {
              setTimeout(() => {
                if (mockSocket.eventHandlers && mockSocket.eventHandlers['joined']) {
                  mockSocket.eventHandlers['joined']({ sessionId: data.sessionId });
                }
              }, 100);
            }
          },
          disconnect: () => {
            console.log('Mock socket disconnected');
          },
          // Mock method to trigger events
          mockEmit: (event, data) => {
            if (mockSocket.eventHandlers && mockSocket.eventHandlers[event]) {
              mockSocket.eventHandlers[event](data);
            }
          },
          eventHandlers: {}
        };
        
        // Auto-trigger connect event for mock socket
        setTimeout(() => {
          if (mockSocket.eventHandlers && mockSocket.eventHandlers['connect']) {
            mockSocket.eventHandlers['connect']();
          }
        }, 100);
        
        return mockSocket;
      }
      
      return null;
    }
  }, [dispatch]);
  
  // Start WebRTC connection process
  const connect = useCallback(async () => {
    console.log('Starting WebRTC connection process...');

    if (process.env.NODE_ENV === 'development' && window.__WEBRTC_DEMO_CONNECTION_ACTIVE) {
      const preserved = window.__WEBRTC_DEMO_CONNECTION_ACTIVE;
      if (preserved.connectionState === 'connected' || preserved.connectionState === 'connecting') {
        console.log('HMR: Attempting to restore existing connection...');
        if (preserved.sipUser) setSipUser(preserved.sipUser);
        if (preserved.sipSession) setSipSession(preserved.sipSession);
        if (preserved.mediaStream) setMediaStream(preserved.mediaStream);
        if (preserved.audioContext) setAudioContext(preserved.audioContext); // Restore AC
        if (document.querySelector('audio#dasha-remote-audio.hmr-preserved')) {
          remoteAudioRef.current = document.querySelector('audio#dasha-remote-audio.hmr-preserved');
          remoteAudioRef.current.classList.remove('hmr-preserved');
          console.log('HMR: Restored preserved remote audio element.');
        }
        dispatch({ type: 'SET_CONNECTION_STATE', payload: preserved.connectionState });
        if (preserved.connectionState === 'connected') {
          setIsCallSetupComplete(true);
          // Re-initialize analyser for local audio if stream exists
          if (mediaStreamRef.current) initAudioAnalyzer(mediaStreamRef.current); 
          // If remote audio is playing, AI audio levels should resume via its own analyser setup in setupRemoteAudio
          console.log('HMR: Connection restored. State:', preserved.connectionState);
          return;
        }
      }
      window.__WEBRTC_DEMO_CONNECTION_ACTIVE = null; // Clear if not fully restorable
    }

    dispatch({ type: 'SET_CONNECTION_STATE', payload: 'connecting' });
    reconnectAttemptsRef.current = 0; // Reset on new connection attempt

    const micCheck = await checkMicrophonePermission();
    if (!micCheck.success || !micCheck.stream) {
      dispatch({ type: 'SET_CONNECTION_STATE', payload: 'error' });
      // Error payload is set by checkMicrophonePermission
      return;
    }
    // setMediaStream(micCheck.stream); // Already set within checkMicrophonePermission

    try {
      const credentials = await getSipCredentials();
      initAudioAnalyzer(micCheck.stream); // Initialize analyzer for local audio

      const sipConfig = {
        aor: credentials.aor,
        media: {
          constraints: { audio: true, video: false },
          local: { stream: micCheck.stream },
        },
        userAgentOptions: { displayName: userName || 'User' },
      };

      const user = new Web.SimpleUser(credentials.sipServerEndpoint, sipConfig);
      await user.connect();
      console.log('SIP User connected to signaling server.');
      setSipUser(user); // This will trigger sipUserRef update

      try {
        await user.register();
        console.log('SIP User registered.');
      } catch (regError) {
        console.warn('SIP registration failed (continuing):', regError);
      }

      const conversation = await initiateConversation(credentials.aor);
      console.log('Server conversation initiated.');

      const sessionIdMatch = credentials.aor.match(/sip:reg-(.*?)@/);
      const conversationId = conversation.conversationId || sessionIdMatch?.[1] || (process.env.NODE_ENV !== 'production' && conversation.mock ? `mock-${Date.now()}` : null);
      if (conversationId) {
        const newSocket = setupTranscriptSocket(conversationId);
        if (newSocket) setSocket(newSocket);
      } else {
        console.warn('No conversation ID for transcript socket.');
      }

      if (process.env.NODE_ENV !== 'production' && conversation.mock) {
        console.log('Dev mode: Simulating incoming call & connection.');
        dispatch({ type: 'SET_CONNECTION_STATE', payload: 'connected' });
        setSipSession(user.session || {}); // Simulate a session object if simpleUser doesn't auto-create one in mock
        setIsCallSetupComplete(true);
        // Simulate AI greeting via mock socket if available
        // ... (keep AI greeting simulation logic)
        return;
      }

      user.delegate = {
        onCallReceived: async () => {
          try {
            console.log('Incoming call received, answering...');
            await user.answer();
            const currentActiveSession = user.session;
            if (currentActiveSession) {
              console.log('Call answered. Active session ID:', currentActiveSession.id);
              setSipSession(currentActiveSession);
              setupRemoteAudio(currentActiveSession);
              dispatch({ type: 'SET_CONNECTION_STATE', payload: 'connected' });
              setIsCallSetupComplete(true);
            } else {
              console.error('Call answered, but user.session is not valid.');
              dispatch({ type: 'SET_ERROR', payload: { message: 'Error establishing call: Invalid session.' } });
              dispatch({ type: 'SET_CONNECTION_STATE', payload: 'error' });
              cleanupResources();
            }
          } catch (answerError) {
            console.error('Error answering call:', answerError);
            dispatch({ type: 'SET_ERROR', payload: { message: `Call answer error: ${answerError.message}` } });
            dispatch({ type: 'SET_CONNECTION_STATE', payload: 'error' });
            cleanupResources();
          }
        },
        onCallHangup: () => {
          console.log('Call ended by remote (onCallHangup).');
          dispatch({ type: 'ADD_TRANSCRIPT_ITEM', payload: { cmd: 'log', text: 'Call ended by AI.' } });
          cleanupResources();
          dispatch({ type: 'SET_CONNECTION_STATE', payload: 'disconnected' });
        },
      };
    } catch (error) {
      console.error('Connection process failed:', error);
      if (reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
        reconnectAttemptsRef.current++;
        dispatch({ type: 'ADD_TRANSCRIPT_ITEM', payload: { cmd: 'log', text: `Connection failed. Retrying (${reconnectAttemptsRef.current}/${MAX_RECONNECT_ATTEMPTS})...` } });
        setTimeout(connect, RECONNECT_DELAY);
      } else {
        dispatch({ type: 'SET_ERROR', payload: { message: error.message || 'Connection failed after multiple retries.' } });
        dispatch({ type: 'SET_CONNECTION_STATE', payload: 'error' });
        cleanupResources(); // Ensure cleanup if retries exhausted
      }
    }
  }, [
    checkMicrophonePermission, dispatch, getSipCredentials, initAudioAnalyzer,
    initiateConversation, setupRemoteAudio, setupTranscriptSocket, userName, 
    // No direct state dependencies, they are accessed via refs or set by setters from this scope
  ]);
  
  // Clean up all resources
  const cleanupResources = useCallback(() => {
    console.log('Cleaning up WebRTC resources...');
    if (audioLevelIntervalRef.current) {
      clearInterval(audioLevelIntervalRef.current);
      audioLevelIntervalRef.current = null;
    }

    const currentSipUser = sipUserRef.current;
    if (currentSipUser) {
      console.log('Disconnecting SIP user...');
      currentSipUser.disconnect().catch(e => console.error('Error during sipUser.disconnect():', e));
      // setSipUser(null); // This will be handled by the main useEffect or if connect() fails
    }
    setSipUser(null); // Explicitly clear sipUser state
    setSipSession(null); // Explicitly clear sipSession state

    setIsCallSetupComplete(false);
    setIsMuted(false);

    if (socket) {
      console.log('Disconnecting WebSocket...');
      socket.disconnect();
      setSocket(null);
    }

    const currentAudioContext = audioContextRef.current;
    if (currentAudioContext && currentAudioContext.state !== 'closed') {
      console.log('Closing AudioContext...');
      currentAudioContext.close().catch(e => console.error('Error closing AudioContext:', e));
    }
    setAudioContext(null);
    setAudioAnalyser(null);

    const currentRemoteAudioEl = remoteAudioRef.current;
    if (currentRemoteAudioEl && currentRemoteAudioEl.srcObject) {
      console.log('Stopping remote audio tracks...');
      currentRemoteAudioEl.srcObject.getTracks().forEach(track => track.stop());
      currentRemoteAudioEl.srcObject = null;
    }
    // HMR preservation might remove/re-add, so direct removal from DOM is tricky here.
    // If not HMR, it should be fine if a new one is created on next connect.

    const currentMediaStream = mediaStreamRef.current;
    if (currentMediaStream) {
      console.log('Stopping local media tracks...');
      currentMediaStream.getTracks().forEach(track => track.stop());
      setMediaStream(null);
    }
    console.log('Finished cleaning WebRTC resources.');
  }, [dispatch]); // Only dispatch is a direct dependency from outside, others are refs or setters.
  
  // Disconnect WebRTC - user initiated
  const disconnect = useCallback(async () => {
    console.log('User initiated disconnect.');
    dispatch({ type: 'SET_CONNECTION_STATE', payload: 'disconnected' });
    dispatch({ type: 'ADD_TRANSCRIPT_ITEM', payload: { cmd: 'log', text: 'Call ended by user.' } });

    const currentSipUser = sipUserRef.current;
    if (currentSipUser && currentSipUser.session) {
      console.log('Hanging up active call via sipUser.hangup()...');
      try {
        await currentSipUser.hangup();
        console.log('Call hangup successful.');
      } catch (e) {
        console.error('Error during sipUser.hangup():', e);
      }
    } else if (currentSipUser) {
      console.log('No active session, ensuring sipUser is disconnected (already called by cleanup).');
      // currentSipUser.disconnect().catch(e => console.error('Error during sipUser.disconnect() in disconnect flow:', e));
    }
    cleanupResources();
  }, [dispatch, cleanupResources]); // sipUserRef is used
  
  // Set up cleanup on unmount
  useEffect(() => {
    const hmrTimestamp = Date.now();
    const unmountId = Math.random().toString(36).substring(7);
    // console.log(`useWebRTC mounted (ID: ${unmountId})`);

    return () => {
      // console.log(`useWebRTC unmounting (ID: ${unmountId}, State: ${connectionStateRef.current})`);
      const isLikelyHmrReload = process.env.NODE_ENV === 'development' && (Date.now() - hmrTimestamp < 3000); // Shorter HMR window

      if (process.env.NODE_ENV === 'development' && isLikelyHmrReload && connectionStateRef.current !== 'disconnected') {
        console.log(`HMR: Preserving connection state (ID: ${unmountId})`);
        window.__WEBRTC_DEMO_CONNECTION_ACTIVE = {
          sipUser: sipUserRef.current,
          sipSession: sipSessionRef.current, 
          connectionState: connectionStateRef.current,
          mediaStream: mediaStreamRef.current,
          audioContext: audioContextRef.current,
        };
        if (remoteAudioRef.current && remoteAudioRef.current.srcObject) {
          remoteAudioRef.current.classList.add('hmr-preserved');
          // Ensure it's in the body to survive component tree removal by HMR
          if (remoteAudioRef.current.parentNode !== document.body) {
             document.body.appendChild(remoteAudioRef.current);
          }
          console.log('HMR: Marked remote audio element for preservation.');
        }
      } else {
        console.log(`Full cleanup on unmount (ID: ${unmountId}, Not HMR / Production / or Disconnected)`);
        cleanupResources();
      }
    };
  }, [cleanupResources]); // Only stable cleanupResources needed here.
  
  // Toggle microphone mute status
  const toggleMute = useCallback(() => {
    // Use sipUser.session as it's the active session object managed by SimpleUser
    const activeSession = sipUser?.session;

    if (!activeSession || !activeSession.sessionDescriptionHandler || !activeSession.sessionDescriptionHandler.peerConnection) {
      console.warn('Active SIP session, session description handler, or peer connection not available to toggle mute.');
      // If isCallSetupComplete was true, this state is unexpected. Log more details if possible.
      if (isCallSetupComplete) {
        console.error('toggleMute called when isCallSetupComplete is true, but session components are missing.', 
          { hasSipUser: !!sipUser, activeSession, pcAvailable: !!activeSession?.sessionDescriptionHandler?.peerConnection });
      }
      return;
    }

    const pc = activeSession.sessionDescriptionHandler.peerConnection;
    const senders = pc.getSenders();
    const audioSender = senders.find(s => s.track && s.track.kind === 'audio');

    if (audioSender && audioSender.track) {
      const newMutedState = !isMuted;
      audioSender.track.enabled = !newMutedState; // If going to be muted, disable track. If unmuting, enable track.
      setIsMuted(newMutedState);
      console.log(`Microphone track ${audioSender.track.enabled ? 'enabled (unmuted)' : 'disabled (muted)'}. Local isMuted state: ${newMutedState}`);
      if (newMutedState) {
        dispatch({ type: 'SET_USER_AUDIO_LEVEL', payload: 0 });
      }
      // No need to adjust AI audio level, as we are only controlling our outgoing microphone.
    } else {
      console.warn('No local audio sender track found to mute/unmute.');
      // As a fallback, try to manipulate the original mediaStream directly if it exists
      // This might not be effective if sip.js has full control over a cloned track
      if (mediaStream) {
        console.warn('Attempting fallback: toggling tracks on original mediaStream.');
        const audioTracks = mediaStream.getAudioTracks();
        if (audioTracks.length > 0) {
          const newMutedState = !isMuted;
          audioTracks.forEach(track => {
            track.enabled = !newMutedState;
          });
          setIsMuted(newMutedState);
          console.log(`Fallback: Microphone tracks on mediaStream ${!newMutedState ? 'enabled (unmuted)' : 'disabled (muted)'}. Local isMuted state: ${newMutedState}`);
          if (newMutedState) {
            dispatch({ type: 'SET_USER_AUDIO_LEVEL', payload: 0 });
          }
        } else {
          console.warn('Fallback: No audio tracks found on original mediaStream.');
        }
      } else {
        console.warn('Fallback: Original mediaStream is not available.');
      }
    }
  }, [sipUser, mediaStream, isMuted, dispatch, isCallSetupComplete]);
  
  return {
    connect,
    disconnect,
    toggleMute,
    microphoneAccess,
    audioAnalyser,
    isMuted,
    isCallSetupComplete,
    sipUser,
    sipSession
  };
};

export default useWebRTC;